import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Clock, Shield, Users, Video } from 'lucide-react';
import { Button } from '../components/ui/button';
import TrustBadges from '../components/TrustBadges';
import { mockHowItWorks, mockPricing, mockServices, mockFAQs } from '../mockData';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';

const Home = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#F0F5FF] via-white to-[#FFF0F7] py-20 md:py-32 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="inline-flex items-center space-x-2 bg-white px-4 py-2 rounded-full border-2 border-[#0066FF] shadow-md">
                <Shield className="text-[#0066FF]" size={16} />
                <span className="text-sm font-semibold text-[#0066FF]">HIPAA-Aligned & Secure</span>
              </div>
              
              <h1 className="text-5xl md:text-6xl font-bold text-[#1A202C] leading-tight">
                Quality Healthcare
                <span className="bg-gradient-to-r from-[#0066FF] to-[#FF0099] bg-clip-text text-transparent"> Anytime, Anywhere</span>
              </h1>
              
              <p className="text-xl text-[#4A5568] leading-relaxed">
                Connect with licensed providers through secure video visits. Choose from Medicaid, insurance, self-pay, or membership options.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  size="lg"
                  className="bg-gradient-to-r from-[#0066FF] to-[#0052CC] hover:from-[#0052CC] hover:to-[#0066FF] text-white px-8 py-6 text-lg font-semibold rounded-xl transition-all duration-300 shadow-lg hover:shadow-2xl transform hover:scale-105"
                >
                  Start a Visit Now
                  <ArrowRight className="ml-2" size={20} />
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="border-2 border-[#FF0099] text-[#FF0099] hover:bg-[#FF0099] hover:text-white px-8 py-6 text-lg font-semibold rounded-xl transition-all duration-300 shadow-md hover:shadow-xl"
                >
                  Check Coverage
                </Button>
              </div>
              
              <div className="flex items-center space-x-6 pt-4">
                <div className="flex items-center space-x-2">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#0066FF] to-[#00A3FF] flex items-center justify-center">
                    <Clock className="text-white" size={20} />
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-[#1A202C]">10 mins</div>
                    <div className="text-xs text-[#718096]">Average wait</div>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#FF0099] to-[#FF66CC] flex items-center justify-center">
                    <Users className="text-white" size={20} />
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-[#1A202C]">24/7</div>
                    <div className="text-xs text-[#718096]">Availability</div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="relative">
              <div className="relative z-10 bg-white rounded-3xl shadow-2xl p-8 border border-gray-100">
                <div className="aspect-video bg-gradient-to-br from-[#0066FF] via-[#0099FF] to-[#FF0099] rounded-2xl flex items-center justify-center relative overflow-hidden">
                  <div className="absolute inset-0 bg-white/10 backdrop-blur-sm"></div>
                  <div className="text-center text-white relative z-10">
                    <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-xl">
                      <Video className="text-[#0066FF]" size={40} />
                    </div>
                    <p className="text-lg font-bold">Secure Video Visits</p>
                  </div>
                </div>
                <div className="mt-6 space-y-3">
                  <div className="flex items-center space-x-3">
                    <div className="w-6 h-6 rounded-full bg-gradient-to-br from-[#0066FF] to-[#00A3FF] flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="text-white" size={16} />
                    </div>
                    <span className="text-sm text-[#4A5568] font-medium">Board-certified providers</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-6 h-6 rounded-full bg-gradient-to-br from-[#0066FF] to-[#00A3FF] flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="text-white" size={16} />
                    </div>
                    <span className="text-sm text-[#4A5568] font-medium">Encrypted connections</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-6 h-6 rounded-full bg-gradient-to-br from-[#0066FF] to-[#00A3FF] flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="text-white" size={16} />
                    </div>
                    <span className="text-sm text-[#4A5568] font-medium">Prescriptions sent to pharmacy</span>
                  </div>
                </div>
              </div>
              <div className="absolute -top-8 -right-8 w-40 h-40 bg-gradient-to-br from-[#FF0099] to-[#FF66CC] rounded-full opacity-20 blur-3xl animate-pulse"></div>
              <div className="absolute -bottom-8 -left-8 w-48 h-48 bg-gradient-to-br from-[#0066FF] to-[#00A3FF] rounded-full opacity-20 blur-3xl animate-pulse" style={{animationDelay: '1s'}}></div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Badges Section */}
      <section className="py-16 bg-white border-t border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <TrustBadges />
        </div>
      </section>

      {/* Medicaid Acceptance Section */}
      <section className="py-16 bg-gradient-to-br from-[#F0F5FF] to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-r from-[#0066FF] to-[#FF0099] rounded-3xl p-12 md:p-16 shadow-2xl">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div className="text-white">
                <div className="inline-flex items-center space-x-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
                  <Shield className="text-white" size={20} />
                  <span className="text-sm font-semibold">Medicaid Accepted</span>
                </div>
                <h2 className="text-4xl md:text-5xl font-bold mb-6">
                  We Accept Medicaid
                </h2>
                <p className="text-xl text-white/90 mb-8 leading-relaxed">
                  Quality healthcare is accessible to everyone. We proudly accept Medicaid in select states, making our telehealth services available to more families.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Button
                    size="lg"
                    className="bg-white text-[#0066FF] hover:bg-gray-100 px-8 py-6 text-lg font-semibold rounded-xl transition-all duration-200 shadow-xl hover:shadow-2xl"
                  >
                    Check Your Eligibility
                    <ArrowRight className="ml-2" size={20} />
                  </Button>
                  <Button
                    size="lg"
                    variant="outline"
                    className="border-2 border-white text-white hover:bg-white/10 px-8 py-6 text-lg font-semibold rounded-xl transition-all duration-200"
                  >
                    Learn More
                  </Button>
                </div>
              </div>
              <div className="hidden md:block">
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
                  <div className="space-y-4">
                    <div className="flex items-center space-x-3 text-white">
                      <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                        <CheckCircle className="text-white" size={20} />
                      </div>
                      <span className="text-lg">No copay in most Medicaid plans</span>
                    </div>
                    <div className="flex items-center space-x-3 text-white">
                      <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                        <CheckCircle className="text-white" size={20} />
                      </div>
                      <span className="text-lg">All services fully covered</span>
                    </div>
                    <div className="flex items-center space-x-3 text-white">
                      <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                        <CheckCircle className="text-white" size={20} />
                      </div>
                      <span className="text-lg">Easy verification process</span>
                    </div>
                    <div className="flex items-center space-x-3 text-white">
                      <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                        <CheckCircle className="text-white" size={20} />
                      </div>
                      <span className="text-lg">Available in multiple states</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 bg-[#F4F6F8]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#12324A] mb-4">
              How It Works
            </h2>
            <p className="text-xl text-[#5F6C7B] max-w-2xl mx-auto">
              Get care in three simple steps. Fast, secure, and convenient.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {mockHowItWorks.map((item, index) => (
              <div key={item.id} className="relative">
                <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 h-full">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#0F4C81] to-[#1FB6B2] flex items-center justify-center mb-6">
                    <span className="text-2xl font-bold text-white">{item.step}</span>
                  </div>
                  <h3 className="text-2xl font-semibold text-[#12324A] mb-4">
                    {item.title}
                  </h3>
                  <p className="text-[#5F6C7B] leading-relaxed">
                    {item.description}
                  </p>
                </div>
                {index < mockHowItWorks.length - 1 && (
                  <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-10">
                    <ArrowRight className="text-[#1FB6B2]" size={32} />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#12324A] mb-4">
              Choose Your Care Option
            </h2>
            <p className="text-xl text-[#5F6C7B] max-w-2xl mx-auto">
              Transparent pricing that fits your needs. No hidden fees.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {mockPricing.map((plan) => (
              <div
                key={plan.id}
                className={`rounded-2xl p-8 border-2 transition-all duration-300 hover:shadow-xl ${
                  plan.highlighted
                    ? 'border-[#0F4C81] bg-gradient-to-br from-[#F4F6F8] to-white shadow-lg scale-105'
                    : 'border-gray-200 bg-white hover:border-[#1FB6B2]'
                }`}
              >
                {plan.highlighted && (
                  <div className="inline-block px-4 py-1 bg-[#0F4C81] text-white text-sm font-semibold rounded-full mb-4">
                    Most Popular
                  </div>
                )}
                <h3 className="text-2xl font-bold text-[#12324A] mb-2">{plan.title}</h3>
                <p className="text-[#5F6C7B] mb-6">{plan.description}</p>
                <div className="mb-6">
                  <span className="text-4xl font-bold text-[#0F4C81]">{plan.price}</span>
                </div>
                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, index) => (
                    <li key={index} className="flex items-start space-x-3">
                      <CheckCircle className="text-[#1FB6B2] flex-shrink-0 mt-0.5" size={20} />
                      <span className="text-[#5F6C7B]">{feature}</span>
                    </li>
                  ))}
                </ul>
                <Button
                  className={`w-full py-6 text-lg font-semibold rounded-xl transition-all duration-200 ${
                    plan.highlighted
                      ? 'bg-[#0F4C81] hover:bg-[#12324A] text-white shadow-lg'
                      : 'bg-white border-2 border-[#0F4C81] text-[#0F4C81] hover:bg-[#0F4C81] hover:text-white'
                  }`}
                >
                  {plan.buttonText}
                </Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="py-20 bg-[#F4F6F8]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#12324A] mb-4">
              What We Treat
            </h2>
            <p className="text-xl text-[#5F6C7B] max-w-2xl mx-auto">
              Comprehensive healthcare services from licensed providers
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            {mockServices.slice(0, 6).map((service) => (
              <div
                key={service.id}
                className="bg-white rounded-xl p-6 border border-gray-200 hover:border-[#1FB6B2] hover:shadow-lg transition-all duration-300"
              >
                <h3 className="text-xl font-semibold text-[#12324A] mb-3">{service.title}</h3>
                <p className="text-[#5F6C7B]">{service.description}</p>
              </div>
            ))}
          </div>
          
          <div className="text-center">
            <Link to="/services">
              <Button
                variant="outline"
                size="lg"
                className="border-2 border-[#0F4C81] text-[#0F4C81] hover:bg-[#0F4C81] hover:text-white px-8 py-6 text-lg font-semibold rounded-xl"
              >
                View All Services
                <ArrowRight className="ml-2" size={20} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ Preview */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#12324A] mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-[#5F6C7B]">
              Get answers to common questions about our services
            </p>
          </div>
          
          <Accordion type="single" collapsible className="space-y-4">
            {mockFAQs.slice(0, 6).map((faq) => (
              <AccordionItem
                key={faq.id}
                value={`item-${faq.id}`}
                className="border border-gray-200 rounded-xl px-6 bg-white hover:border-[#1FB6B2] transition-colors"
              >
                <AccordionTrigger className="text-left font-semibold text-[#12324A] hover:text-[#0F4C81] py-6">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-[#5F6C7B] pb-6">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          
          <div className="text-center mt-8">
            <Link to="/faq">
              <Button
                variant="outline"
                size="lg"
                className="border-2 border-[#0F4C81] text-[#0F4C81] hover:bg-[#0F4C81] hover:text-white px-8 py-6 text-lg font-semibold rounded-xl"
              >
                View All FAQs
                <ArrowRight className="ml-2" size={20} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-br from-[#0F4C81] to-[#12324A] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-xl mb-8 text-gray-200">
            Connect with a licensed provider in minutes. Quality care is just a click away.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              size="lg"
              className="bg-white text-[#0F4C81] hover:bg-gray-100 px-8 py-6 text-lg font-semibold rounded-xl transition-all duration-200 shadow-lg"
            >
              Start a Visit Now
              <ArrowRight className="ml-2" size={20} />
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="border-2 border-white text-white hover:bg-white hover:text-[#0F4C81] px-8 py-6 text-lg font-semibold rounded-xl transition-all duration-200"
            >
              Learn More
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
